// src/components/LocalizedLink.tsx
import Link, { LinkProps } from "next/link";
import React, { useMemo } from "react";

import { useLanguage } from "@/context/TranslationsContext";
import { defaultLanguage } from "@/translations/supportedLanguages";

type LocalizedLinkProps = LinkProps & {
  children: React.ReactNode;
  className?: string;
};

export default function LocalizedLink({
  href,
  children,
  className,
  ...props
}: LocalizedLinkProps) {
  const language = useLanguage();

  const finalHref = useMemo(() => {
    let normalized = "/";

    if (typeof href === "string") {
      normalized = href.startsWith("/") ? href : `/${href}`;
    } else if (href.pathname) {
      normalized = href.pathname.startsWith("/")
        ? href.pathname
        : `/${href.pathname}`;
    }

    if (normalized === `/${defaultLanguage.code}`) normalized = "/";

    const isRoot = normalized === "/";
    const alreadyLocalized =
      normalized === `/${language}` || normalized.startsWith(`/${language}/`);

    const localizedPath = alreadyLocalized
      ? normalized
      : isRoot
        ? language === defaultLanguage.code
          ? "/"
          : `/${language}`
        : language === defaultLanguage.code
          ? normalized
          : `/${language}${normalized}`;

    /*  Keep object identity stable when href is an object  */
    return typeof href === "string"
      ? localizedPath
      : ({ ...href, pathname: localizedPath } as const);
  }, [href, language]);

  return (
    <Link href={finalHref} className={className} {...props}>
      {children}
    </Link>
  );
}
